import { useEffect } from 'react'
import { GlobalEvents } from '@highlight/ipc'
import { OVERLAY_CHAT_INPUT_COMPOSING_EVENT } from '@shared/overlay-window-level'

/**
 * Publishes whether any element in `inputElementIds` (or a descendant)
 * currently has focus, and whether an IME composition is active inside it.
 * The main process uses these to decide when the overlay window may drop
 * below the macOS text-popup layer (see `OverlayWindow.applyWindowLevel`).
 *
 * Main keeps one focused flag, so a second instance would overwrite this
 * one's state. Pass every overlay text input to a single call.
 *
 * The signals only gate the level drop. Window focus in the main process
 * remains the authority, so a stale value can never leave the overlay
 * stranded at a lowered level. A transiently-false value only occurs while
 * the input is not focused, which is exactly when no text popup can appear.
 *
 * No-op in non-overlay renderers (the chat input is reused elsewhere).
 */
export function usePublishOverlayInputFocus(inputElementIds: ReadonlyArray<string>) {
  const inputSelector = inputElementIds.map((id) => `#${id}`).join(', ')

  useEffect(() => {
    if (typeof window === 'undefined' || window.__RENDERER_NAME !== 'overlay') {
      return
    }

    let lastPublishedFocus: boolean | null = null
    let lastPublishedComposing = false

    const publishFocus = (isFocused: boolean) => {
      if (isFocused === lastPublishedFocus) {
        return
      }
      lastPublishedFocus = isFocused
      window.ipc.setChatInputFocused(isFocused)
    }

    // Targeted invoke: only main listens, and it must arrive before the candidate window paints.
    const publishComposing = (isComposing: boolean) => {
      if (isComposing === lastPublishedComposing) {
        return
      }
      lastPublishedComposing = isComposing
      void GlobalEvents.invoke(OVERLAY_CHAT_INPUT_COMPOSING_EVENT, isComposing).catch((error) => {
        console.error('[Overlay] chat input composing publish failed:', error)
      })
    }

    const isWithinInput = (target: EventTarget | null) => {
      return target instanceof Element && Boolean(target.closest(inputSelector))
    }

    const handleFocusIn = (event: FocusEvent) => {
      if (isWithinInput(event.target)) {
        publishFocus(true)
      }
    }

    const handleFocusOut = (event: FocusEvent) => {
      if (isWithinInput(event.target) && !isWithinInput(event.relatedTarget)) {
        publishFocus(false)
        publishComposing(false)
      }
    }

    const handleCompositionStart = (event: CompositionEvent) => {
      if (isWithinInput(event.target)) {
        publishComposing(true)
      }
    }

    const handleCompositionEnd = (event: CompositionEvent) => {
      if (isWithinInput(event.target)) {
        publishComposing(false)
      }
    }

    publishFocus(isWithinInput(document.activeElement))
    document.addEventListener('focusin', handleFocusIn, true)
    document.addEventListener('focusout', handleFocusOut, true)
    document.addEventListener('compositionstart', handleCompositionStart, true)
    document.addEventListener('compositionend', handleCompositionEnd, true)

    return () => {
      document.removeEventListener('focusin', handleFocusIn, true)
      document.removeEventListener('focusout', handleFocusOut, true)
      document.removeEventListener('compositionstart', handleCompositionStart, true)
      document.removeEventListener('compositionend', handleCompositionEnd, true)
      publishFocus(false)
      publishComposing(false)
    }
  }, [inputSelector])
}
